// src/pages/ViewOrders.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { ArrowLeft, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { GetSingleInvoiceThunk } from "../../redux/states/payments/thunk";

const ViewPayments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singlePayment, loading } = useSelector((state) => state.payments);

  useEffect(() => {
    dispatch(GetSingleInvoiceThunk(uuid));
  }, [dispatch, uuid]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading payment details...
            </p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singlePayment) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
          <XCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Payment Not Found
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold"
          >
            Back
          </button>
        </div>
      </NonAuthLayout>
    );
  }

  const payment = singlePayment;

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return { color: "bg-green-100 text-green-800", icon: CheckCircle };
      case "inactive":
        return { color: "bg-red-100 text-red-800", icon: XCircle };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: AlertCircle };
    }
  };

  const StatusBadge = getStatusBadge(payment.status);
  const StatusIcon = StatusBadge.icon;

  return (
    <NonAuthLayout>
      <div className="min-h-screen py-2 px-2 sm:px-2 lg:px-2">
        <div className="max-w-12xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
            >
              <ArrowLeft />
              Back
            </button>
            <h1 className="text-small font-bold text-gray-800">
              Payment Details
            </h1>
            <span
              className={`px-3 py-1 rounded-full flex items-center gap-1 ${StatusBadge.color}`}
            >
              <StatusIcon className="w-4 h-4" />
              {payment.status}
            </span>
          </div>

          {/* Payment Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Payment Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label="UUID" value={payment.uuid} />
              <InfoRow label="Order ID" value={payment.order_id} />
              <InfoRow label="Order Number" value={payment.order_id_data} />
              <InfoRow label="Payment Method" value={payment.method} />
              <InfoRow label="Transaction ID" value={payment.transaction_id} />
              <InfoRow label="Amount" value={`₹${payment.amount}`} />
              <InfoRow label="Payment Status" value={payment.payment_status} />
              <InfoRow label="Payment Date" value={payment.payment_date} />
              <InfoRow label="Remarks" value={payment.remarks} />
            </div>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

// ---------------- Helper Component ----------------
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
    <span className="font-medium text-gray-700">{label}</span>
    <span className="text-gray-900">{value || "—"}</span>
  </div>
);

export default ViewPayments;
